import { bigFromDecimal, percentageValue } from 'utils/price'
import moment, { Moment } from 'moment-timezone'
import { useCallback, useMemo, useState } from 'react'
import { useGetOrder, useRescheduleOrder } from 'dataQueries'

import Big from 'big.js'
import { ISODateString } from 'models/misc'
import constate from 'constate'
import { useDynamicNow } from 'utils/hooks'
import { useTimezone } from 'components/contexts/timezone.context'

export const freeRescheduleTimeLimit = process.env.REACT_APP_FREE_RESCHEDULE_HOUR_LIMIT ?? 24

export enum RescheduleReason {
  REQUESTED_BY_CT = 'REQUESTED_BY_CT',
  WEATHER = 'WEATHER',
  ILLNESS = 'ILLNESS',
  OTHER = 'OTHER',
}

export enum RescheduleStep {
  FORM = 'FORM',
  CONFIRM = 'CONFIRM',
}

const _getTime = (dateString: ISODateString | null): number => {
  if (!dateString) return Number.POSITIVE_INFINITY

  return new Date(dateString).getTime()
}

export const [RescheduleOrderModuleProvider, useRescheduleOrderModule] = constate(({ orderId }: { orderId: number | undefined }) => {

  const orderQuery = useGetOrder(orderId ?? 0, !!orderId, true)
  const { userTimezone } = useTimezone()
  const now = useDynamicNow(userTimezone)

  const rescheduleMutation = useRescheduleOrder()
  const [rescheduleReason, setRescheduleReason] = useState<RescheduleReason>()
  const [rescheduleReasonComment, setRescheduleReasonComment] = useState<string>('')
  const [newDate, setNewDate] = useState<Moment | null>(null)
  const [creativeAgreed, setCreativeAgreed] = useState<boolean>()
  const [step, setStep] = useState<RescheduleStep>(RescheduleStep.FORM)

  const orderData = useMemo(() => orderQuery.data?.data, [orderQuery.data?.data])
  const assignments = useMemo(() => orderData?.missions ?? [], [orderData?.missions])
  const assignedCT = useMemo(() => assignments.find((assignment) => !!assignment.creative)?.creative ?? undefined, [assignments])

  const isMin48Hours = useMemo(() => !assignedCT || !creativeAgreed, [assignedCT, creativeAgreed])

  const penaltyAmount = useMemo<Big>(() => {
    if (!orderData) return new Big(0)

    return percentageValue(orderData.discountedFeePrice, new Big(100).minus(bigFromDecimal(orderData.financials.vat)))
  }, [orderData])

  const shootingStart = useMemo(() => {
    return assignments.sort((a, b) => _getTime(a.shootingStartDateTime) - _getTime(b.shootingStartDateTime))[0].shootingStartDateTime
  }, [assignments])

  const freeRescheduleLimit = useMemo(
    () => moment(shootingStart).subtract(freeRescheduleTimeLimit, 'hours'),
    [shootingStart]
  )

  const isBeforeLimit = useMemo(() => freeRescheduleLimit.isAfter(now), [freeRescheduleLimit, now])

  const hoursBeforeEnd = useMemo(() => moment(shootingStart).diff(now, 'hours'), [now, shootingStart])

  const isPenaltyRequired = useMemo(() => !isBeforeLimit && !!assignedCT, [assignedCT, isBeforeLimit])

  const isReschedulePossible = useMemo(() => {
    if (!shootingStart) return false
    if (now.isSameOrAfter(shootingStart)) return false

    return true
  }, [now, shootingStart])

  const visualProgress = useMemo(() => {
    if (!orderData) return 0
    const start = !!orderData.createdAt ? moment(orderData.createdAt) : moment().subtract(1, 'week')
    const end = moment(shootingStart)

    if (!end) return 10

    if (now.isBefore(freeRescheduleLimit)) {
      return (now.valueOf() - start.valueOf()) / (freeRescheduleLimit.valueOf() - start.valueOf()) * 75
    }

    return Math.min((now.valueOf() - freeRescheduleLimit.valueOf()) / (end.valueOf() - freeRescheduleLimit.valueOf()) * 25 + 75, 100)

  }, [freeRescheduleLimit, now, orderData, shootingStart])

  const dateError = useMemo(() => {
    if (!newDate) return 'required'
    if (newDate.isSame(shootingStart)) return 'sameDate'
    if (isMin48Hours && newDate.isBefore(now.clone().add(48, 'hours'))) return 'minDate'
    return undefined
  }, [newDate, shootingStart, isMin48Hours, now])

  const isCommentError = useMemo(() => rescheduleReason === RescheduleReason.OTHER && rescheduleReasonComment === '', [rescheduleReason, rescheduleReasonComment])

  const isValid = useMemo(() => {
    if (!!dateError || isCommentError || !rescheduleReason) return false
    if (!!assignedCT && creativeAgreed === undefined) return false

    return true
  }, [assignedCT, creativeAgreed, dateError, isCommentError, rescheduleReason])

  const reset = useCallback(() => {
    setRescheduleReason(undefined)
    setRescheduleReasonComment('')
    setNewDate(null)
    setCreativeAgreed(undefined)
    setStep(RescheduleStep.FORM)
  }, [])

  const reschedule = useCallback((onSuccess?: () => void) => {
    if (!isValid) return
    if (!orderId || !newDate || !rescheduleReason) return

    rescheduleMutation.mutate(
      {
        orderId,
        hasCtAgreed: creativeAgreed ?? false,
        newDateTime: newDate.toISOString(true),
        rescheduleComment: rescheduleReasonComment,
        rescheduleReason,
      },
      {
        onSuccess: () => onSuccess?.()
      }
    )
  }, [creativeAgreed, isValid, newDate, orderId, rescheduleMutation, rescheduleReason, rescheduleReasonComment])

  return {
    visualProgress,
    isBeforeLimit,
    freeRescheduleLimit,
    hoursBeforeEnd,
    assignedCT,
    orderData,
    penaltyAmount,
    rescheduleMutation,
    step,
    isMin48Hours,
    rescheduleReason,
    rescheduleReasonComment,
    creativeAgreed,
    newDate,
    dateError,
    isCommentError,
    isValid,
    isPenaltyRequired,
    isReschedulePossible,
    setCreativeAgreed,
    setNewDate,
    setRescheduleReason,
    setRescheduleReasonComment,
    setStep,
    reset,
    reschedule,
  }
})
